import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { Plus, Prohibit, Trash, Buildings, MagnifyingGlass, Check, X as XIcon, Envelope, Copy, Clock } from '@phosphor-icons/react'
import toast from 'react-hot-toast'
import CreateOrganizationModal from '../../components/modals/CreateOrganizationModal'
import LoadingSpinner from '../../components/LoadingSpinner'

export default function SuperAdminOrganizations() {
  const [organizations, setOrganizations] = useState([])
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [actionLoading, setActionLoading] = useState({})
  const [activeTab, setActiveTab] = useState('organizations') // 'organizations' or 'invitations'

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    setLoading(true)
    try {
      await Promise.all([loadOrganizations(), loadInvitations()])
    } finally {
      setLoading(false)
    }
  }

  async function loadOrganizations() {
    try {
      const { data, error } = await supabase
        .from('super_admin_organizations')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setOrganizations(data || [])
    } catch (error) {
      console.error('Error loading organizations:', error)
      toast.error('Failed to load organizations')
    }
  }

  async function loadInvitations() {
    try {
      const { data, error } = await supabase
        .from('org_admin_invitations')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setInvitations(data || [])
    } catch (error) {
      console.error('Error loading invitations:', error)
      toast.error('Failed to load invitations')
    }
  }

  async function handleSuspend(orgId, currentStatus) {
    if (!confirm(`Are you sure you want to ${currentStatus ? 'unsuspend' : 'suspend'} this organization?`)) {
      return
    }

    setActionLoading(prev => ({ ...prev, [orgId]: 'suspend' }))

    try {
      const { error } = await supabase
        .from('organizations')
        .update({ suspended: !currentStatus })
        .eq('id', orgId)

      if (error) throw error

      toast.success(`Organization ${currentStatus ? 'unsuspended' : 'suspended'} successfully`)
      loadOrganizations()
    } catch (error) {
      console.error('Error toggling suspension:', error)
      toast.error('Failed to update organization')
    } finally {
      setActionLoading(prev => {
        const newState = { ...prev }
        delete newState[orgId]
        return newState
      })
    }
  }

  async function handleDelete(orgId, orgName) {
    if (!confirm(`Are you sure you want to DELETE "${orgName}"? This action will soft-delete the organization and it can be restored later.`)) {
      return
    }

    const confirmText = prompt(`Type "${orgName}" to confirm deletion:`)
    if (confirmText !== orgName) {
      toast.error('Organization name does not match. Deletion cancelled.')
      return
    }

    setActionLoading(prev => ({ ...prev, [orgId]: 'delete' }))

    try {
      const { error } = await supabase
        .from('organizations')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', orgId)

      if (error) throw error

      toast.success('Organization deleted successfully')
      loadOrganizations()
    } catch (error) {
      console.error('Error deleting organization:', error)
      toast.error('Failed to delete organization')
    } finally {
      setActionLoading(prev => {
        const newState = { ...prev }
        delete newState[orgId]
        return newState
      })
    }
  }

  async function handleDeleteInvitation(invitationId) {
    if (!confirm('Are you sure you want to delete this invitation?')) {
      return
    }

    setActionLoading(prev => ({ ...prev, [invitationId]: 'delete' }))

    try {
      const { error } = await supabase
        .from('org_admin_invitations')
        .delete()
        .eq('id', invitationId)

      if (error) throw error

      toast.success('Invitation deleted successfully')
      loadInvitations()
    } catch (error) {
      console.error('Error deleting invitation:', error)
      toast.error('Failed to delete invitation')
    } finally {
      setActionLoading(prev => {
        const newState = { ...prev }
        delete newState[invitationId]
        return newState
      })
    }
  }

  function copyInvitationLink(token) {
    const link = `${window.location.origin}/signup/org-admin?token=${token}`
    navigator.clipboard.writeText(link)
    toast.success('Invitation link copied to clipboard!')
  }

  const filteredOrganizations = organizations.filter(org =>
    org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.admin_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.admin_phone?.includes(searchTerm)
  )

 const filteredInvitations = invitations
  .filter(inv => !inv.used) // Only show unused invitations
  .filter(inv =>
    inv.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.org_name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const pendingInvitations = invitations.filter(inv => !inv.used)
  const usedInvitations = invitations.filter(inv => inv.used)

  if (loading) {
    return <LoadingSpinner message="Loading data..." />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Buildings size={32} weight="fill" className="text-blue-600" />
                Platform Management
              </h1>
              <p className="text-gray-600 mt-1">Manage organizations and invitations</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus size={20} weight="bold" />
              Create Invitation
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Organizations</p>
                <p className="text-3xl font-bold text-gray-900">{organizations.length}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Buildings size={24} weight="fill" className="text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Active Orgs</p>
                <p className="text-3xl font-bold text-green-600">
                  {organizations.filter(o => !o.suspended).length}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <Check size={24} weight="bold" className="text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Pending Invites</p>
                <p className="text-3xl font-bold text-yellow-600">
                  {pendingInvitations.length}
                </p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-lg">
                <Clock size={24} weight="fill" className="text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Used Invites</p>
                <p className="text-3xl font-bold text-blue-600">
                  {usedInvitations.length}
                </p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Envelope size={24} weight="fill" className="text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('organizations')}
              className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                activeTab === 'organizations'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Organizations ({organizations.length})
            </button>
            <button
              onClick={() => setActiveTab('invitations')}
              className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                activeTab === 'invitations'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Invitations ({pendingInvitations.length})
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="relative">
            <MagnifyingGlass
              size={20}
              weight="light"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder={activeTab === 'organizations' ? "Search organizations..." : "Search invitations..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Organizations Table */}
        {activeTab === 'organizations' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Organization</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Admin Email</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Phone</th>
                    <th className="text-center py-4 px-6 text-sm font-semibold text-gray-700">Members</th>
                    <th className="text-center py-4 px-6 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-right py-4 px-6 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredOrganizations.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-12 text-center">
                        <Buildings size={48} weight="light" className="text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">
                          {searchTerm ? 'No organizations found' : 'No organizations yet'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredOrganizations.map((org) => (
                      <tr key={org.id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            {org.logo_url ? (
                              <img src={org.logo_url} alt={org.name} className="w-10 h-10 rounded-lg object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                <Buildings size={20} weight="fill" className="text-blue-600" />
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-gray-900">{org.name}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(org.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          {org.admin_email ? (
                            <p className="text-sm text-gray-700">{org.admin_email}</p>
                          ) : (
                            <p className="text-sm text-gray-400 italic">Pending</p>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          <p className="text-sm text-gray-700">{org.admin_phone || '—'}</p>
                        </td>
                        <td className="py-4 px-6 text-center">
                          <span className="inline-flex items-center justify-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                            {org.member_count || 0}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-center">
                          {org.suspended ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                              <Prohibit size={14} weight="fill" />
                              Suspended
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                              <Check size={14} weight="bold" />
                              Active
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleSuspend(org.id, org.suspended)}
                              disabled={actionLoading[org.id]}
                              className={`p-2 rounded-lg transition-colors ${
                                org.suspended ? 'text-green-600 hover:bg-green-50' : 'text-yellow-600 hover:bg-yellow-50'
                              } disabled:opacity-50`}
                              title={org.suspended ? 'Unsuspend' : 'Suspend'}
                            >
                              {actionLoading[org.id] === 'suspend' ? (
                                <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                              ) : org.suspended ? (
                                <Check size={20} weight="bold" />
                              ) : (
                                <Prohibit size={20} weight="fill" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDelete(org.id, org.name)}
                              disabled={actionLoading[org.id]}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Delete"
                            >
                              {actionLoading[org.id] === 'delete' ? (
                                <div className="w-5 h-5 border-2 border-red-300 border-t-red-600 rounded-full animate-spin"></div>
                              ) : (
                                <Trash size={20} weight="fill" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Invitations Table */}
        {activeTab === 'invitations' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Organization</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Email</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Created</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Expires</th>
                    <th className="text-center py-4 px-6 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-right py-4 px-6 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredInvitations.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-12 text-center">
                        <Envelope size={48} weight="light" className="text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">
                          {searchTerm ? 'No invitations found' : 'No invitations yet'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredInvitations.map((inv) => (
                      <tr key={inv.id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            {inv.org_logo_url ? (
                              <img src={inv.org_logo_url} alt={inv.org_name} className="w-10 h-10 rounded-lg object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                <Buildings size={20} weight="fill" className="text-blue-600" />
                              </div>
                            )}
                            <p className="font-semibold text-gray-900">{inv.org_name}</p>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <p className="text-sm text-gray-700">{inv.email}</p>
                        </td>
                        <td className="py-4 px-6">
                          <p className="text-sm text-gray-700">
                            {new Date(inv.created_at).toLocaleDateString()}
                          </p>
                        </td>
                        <td className="py-4 px-6">
                          <p className="text-sm text-gray-700">
                            {new Date(inv.expires_at).toLocaleDateString()}
                          </p>
                        </td>
                        <td className="py-4 px-6 text-center">
                          {inv.used ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                              <Check size={14} weight="bold" />
                              Used
                            </span>
                          ) : new Date(inv.expires_at) < new Date() ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                              <XIcon size={14} weight="bold" />
                              Expired
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                              <Clock size={14} weight="fill" />
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center justify-end gap-2">
                            {!inv.used && (
                              <button
                                onClick={() => copyInvitationLink(inv.token)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Copy Link"
                              >
                                <Copy size={20} weight="bold" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteInvitation(inv.id)}
                              disabled={actionLoading[inv.id]}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Delete"
                            >
                              {actionLoading[inv.id] === 'delete' ? (
                                <div className="w-5 h-5 border-2 border-red-300 border-t-red-600 rounded-full animate-spin"></div>
                              ) : (
                                <Trash size={20} weight="fill" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <CreateOrganizationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={loadData}
      />
    </div>
  )
}